/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { EquipmentsService } from '../equipments.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { Equipment } from 'src/app/_model/equipments';
import { EquipmentsDaoService } from '../../dao/equipments-dao.service';
import { IMSId } from 'src/app/_model/ims-object';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads Equipments
 */
@Injectable()
export class EquipmentsLogicService implements EquipmentsService {
  public equipments$ = new BehaviorSubject<Equipment[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private equipmentsDaoService: EquipmentsDaoService,
    private authenticationService: AuthenticationService,
    private servicejobsService: ServicejobsService,
  ) {
    this.initialize();
  }

  // Public Functions
  public getEquipmentById(id: IMSId): Equipment {
    if (id === undefined) {
      return undefined;
    }

    return this.equipments$.getValue().find((sj) => {
      if (sj.id.imsId === id.imsId && sj.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset() {
    this.equipmentsDaoService.reset();
  }

  public async clear() {
    await this.equipmentsDaoService.clear();
    this.equipments$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteEquipments(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalEquipments();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalEquipments() {
    try {
      this.logger.log(this.constructor.name, '- Load local Equipments');
      let equipments: Equipment[] =
        await this.equipmentsDaoService.getEquipments();

      this.equipments$.next(equipments);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Equipments: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteEquipments(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new Equipments from IMS');

      try {
        let equipments: Equipment[] = await this.loadRemoteEquipmentBatch(syncKey, []);

        this.logger.log(
          this.constructor.name,
          'Finished loading all equipments (' + equipments.length + ' Total)',
        );
        this.equipments$.next(equipments);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Equipments: ',
          error?.message,
        );
        throw error;
      }
    }
  }

  private async loadRemoteEquipmentBatch(
    syncKey: string,
    equipments: Equipment[],
    nextPart?: string
  ): Promise<Equipment[]> {
    let equipmentsData;
    if (nextPart) {
      equipmentsData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey,
          },
        }),
      );
    } else {
      let branchIdsStr = this.servicejobsService.servicejobs$
        .getValue()
        .filter((val) => {
          if (val.branch && val.branch.id.imsId) {
            return true;
          }
          return false;
        })
        .map((val) => {
          return val.branch.id.imsId;
        })
        .join(',');
      equipmentsData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncequipment',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            branchidsstr: branchIdsStr,
            synckey: syncKey
          },
        }),
      );
    }

    let nextId: string;
    let nextEquipments: Equipment[] = [];
    for (const equipmentData of equipmentsData) {
      if (equipmentData['#nextid']) {
        nextId = equipmentData['#nextid'];
      } else {
        nextEquipments.push(Equipment.fromDto(equipmentData));
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch Equipments: ',
      nextEquipments.length,
      'Total',
    );

    await this.equipmentsDaoService.saveEquipments(nextEquipments);

    let returnEquipments = equipments.concat(nextEquipments);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more equipments with nextid',
        nextId,
      );
      return await this.loadRemoteEquipmentBatch(syncKey, returnEquipments, nextId);
    }

    return returnEquipments;
  }

  async initializeCompleted(): Promise<void> {}
}
